import { supabase } from '../supabase/supabaseClient';

/**
 * Ticker Suggestion for Autocomplete
 */
export interface TickerSuggestion {
  ticker: string;
  name: string;
  exchange?: string;
}

/**
 * Parse result for ticker trigger detection
 */
export interface TickerTriggerInfo {
  isTypingTicker: boolean;
  partial: string;
  startPos: number;
}

/**
 * Top 20 Most Traded Stocks (shown when user types $ with no query)
 */
export const TOP_20_TICKERS: TickerSuggestion[] = [
  { ticker: 'AAPL', name: 'Apple Inc.', exchange: 'NASDAQ' },
  { ticker: 'MSFT', name: 'Microsoft Corporation', exchange: 'NASDAQ' },
  { ticker: 'GOOGL', name: 'Alphabet Inc.', exchange: 'NASDAQ' },
  { ticker: 'AMZN', name: 'Amazon.com Inc.', exchange: 'NASDAQ' },
  { ticker: 'NVDA', name: 'NVIDIA Corporation', exchange: 'NASDAQ' },
  { ticker: 'TSLA', name: 'Tesla Inc.', exchange: 'NASDAQ' },
  { ticker: 'META', name: 'Meta Platforms Inc.', exchange: 'NASDAQ' },
  { ticker: 'BRK.B', name: 'Berkshire Hathaway Inc.', exchange: 'NYSE' },
  { ticker: 'JPM', name: 'JPMorgan Chase & Co.', exchange: 'NYSE' },
  { ticker: 'V', name: 'Visa Inc.', exchange: 'NYSE' },
  { ticker: 'WMT', name: 'Walmart Inc.', exchange: 'NYSE' },
  { ticker: 'MA', name: 'Mastercard Inc.', exchange: 'NYSE' },
  { ticker: 'JNJ', name: 'Johnson & Johnson', exchange: 'NYSE' },
  { ticker: 'PG', name: 'Procter & Gamble Co.', exchange: 'NYSE' },
  { ticker: 'XOM', name: 'Exxon Mobil Corporation', exchange: 'NYSE' },
  { ticker: 'UNH', name: 'UnitedHealth Group Inc.', exchange: 'NYSE' },
  { ticker: 'HD', name: 'Home Depot Inc.', exchange: 'NYSE' },
  { ticker: 'CVX', name: 'Chevron Corporation', exchange: 'NYSE' },
  { ticker: 'BAC', name: 'Bank of America Corporation', exchange: 'NYSE' },
  { ticker: 'COST', name: 'Costco Wholesale Corporation', exchange: 'NASDAQ' },
];

/**
 * Parse text to detect $ ticker trigger
 * Returns info about ticker being typed if detected
 */
export const parseTickerTrigger = (
  text: string,
  cursorPosition: number
): TickerTriggerInfo | null => {
  const textBeforeCursor = text.substring(0, cursorPosition);
  const lastDollarSymbol = textBeforeCursor.lastIndexOf('$');

  // No $ symbol found
  if (lastDollarSymbol === -1) {
    return null;
  }

  // Check if there's text after the $
  const textAfterDollar = textBeforeCursor.substring(lastDollarSymbol + 1);

  // If there's a whitespace after $, ticker input was completed
  if (textAfterDollar.includes(' ')) {
    return null;
  }

  // Check if this looks like a ticker (only letters, numbers, dots)
  // Allow: $AAPL, $BRK.B, $lulu, $a
  const isValidTickerChar = /^[a-zA-Z0-9.]*$/.test(textAfterDollar);
  if (!isValidTickerChar) {
    return null;
  }

  return {
    isTypingTicker: true,
    partial: textAfterDollar,
    startPos: lastDollarSymbol,
  };
};

/**
 * Get ticker suggestions based on partial input
 * If partial is empty, returns top 20 popular stocks
 */
export const getTickerSuggestions = async (partial: string): Promise<TickerSuggestion[]> => {
  try {
    // If no partial query, return popular tickers
    if (!partial || partial.length < 1) {
      console.log('📊 Showing top 20 popular tickers');
      return TOP_20_TICKERS;
    }

    // Query database for matches
    console.log(`🔍 Searching for tickers matching: "${partial}"`);

    const { data, error } = await supabase
      .from('company_ticker_mappings')
      .select('ticker, company_name, exchange')
      .or(`ticker.ilike.${partial}%,company_name_lowercase.ilike.%${partial}%`)
      .eq('is_active', true)
      .order('market_cap', { ascending: false, nullsFirst: false })
      .limit(10);

    if (error) {
      console.error('❌ Error fetching ticker suggestions:', error);
      // Fallback to top tickers if database error
      return TOP_20_TICKERS.filter(
        t =>
          t.ticker.toUpperCase().startsWith(partial.toUpperCase()) ||
          t.name.toLowerCase().includes(partial.toLowerCase())
      ).slice(0, 5);
    }

    const suggestions: TickerSuggestion[] = (data || []).map(d => ({
      ticker: d.ticker,
      name: d.company_name,
      exchange: d.exchange || undefined,
    }));

    console.log(`✅ Found ${suggestions.length} ticker suggestions`);
    return suggestions;
  } catch (error) {
    console.error('❌ Unexpected error in getTickerSuggestions:', error);
    return [];
  }
};

/**
 * Insert selected ticker into message text
 * Replaces the partial ticker with the full ticker + space
 */
export const insertTicker = (
  text: string,
  startPos: number,
  cursorPosition: number,
  ticker: string
): { newText: string; newCursorPosition: number } => {
  const tickerText = `$${ticker.toUpperCase()} `;
  const before = text.substring(0, startPos);
  const after = text.substring(cursorPosition);

  const newText = before + tickerText + after;
  const newCursorPosition = startPos + tickerText.length;

  console.log(`✅ Inserted ticker: ${tickerText}`);

  return { newText, newCursorPosition };
};

/**
 * Extract all $ tickers from message text
 * Used to store tickers in message metadata
 */
export const extractTickersFromMessage = (text: string): string[] => {
  const tickerPattern = /\$([A-Z][A-Z0-9.]{0,5})\b/g;
  const matches = Array.from(text.matchAll(tickerPattern));
  const tickers = matches.map(m => m[1]);

  // Remove duplicates
  return [...new Set(tickers)];
};
